import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useExitTransition } from "../hooks/useExitTransition";
import { useDebajoDeMd } from "../hooks/useEsMobile";
import { CuerpoDeConsejos } from "../pages/ConsejosDeFotos";

/**
 * Los consejos de fotos en un pop-up, encima de lo que se esté cargando —
 * ADR-2026-10-14-consejos-en-un-pop-up-desde-varios.
 *
 * "Varios productos a la vez" no tiene borrador: ir a la pantalla de consejos desarmaba la
 * carga, así que con algo cargado "Ver todos los consejos" desaparecía. Abierto acá, no se
 * navega y la carga sigue intacta, así que el botón está siempre. El contenido es el MISMO
 * de la pantalla (`CuerpoDeConsejos`). El envase, el del pop-up de subir de los modos: 980
 * centrado en escritorio, hoja entera en el teléfono, cabecera con título y X.
 *
 * `z-[225]`: por encima del pop-up de subir de los modos (`z-[210]`), que también lo abre.
 */
export const ModalDeConsejos: React.FC<{ abierto: boolean; onCerrar: () => void }> = ({
  abierto,
  onCerrar,
}) => {
  const { mounted, animated } = useExitTransition(abierto, 200);
  const esEscritorio = !useDebajoDeMd();

  useEffect(() => {
    if (!abierto) return;
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Que no lo agarre también el pop-up de abajo (el de subir de los modos).
        e.stopImmediatePropagation();
        onCerrar();
      }
    };
    // En captura: llega antes que el Escape del pop-up de subir.
    window.addEventListener("keydown", alTeclado, true);
    return () => window.removeEventListener("keydown", alTeclado, true);
  }, [abierto, onCerrar]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[225] flex items-end justify-center bg-black/40 p-0 backdrop-blur-[2px] transition-opacity duration-200 ease-out motion-reduce:transition-none md:items-center md:p-4 ${
        animated ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
      // `stopPropagation`: por portal, pero en React el clic sube por quien lo monta (el
      // pop-up de subir, que cerraría).
      onClick={(e) => {
        e.stopPropagation();
        onCerrar();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Cómo sacar buenas fotos"
    >
      <div
        className={`flex w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none md:h-[min(88vh,760px)] md:max-w-[980px] md:rounded-[14px] md:border md:border-neutral-200 ${
          animated
            ? "translate-y-0 md:scale-100 md:opacity-100"
            : "translate-y-full md:translate-y-0 md:scale-95 md:opacity-0"
        }`}
        style={
          esEscritorio
            ? undefined
            : { height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))" }
        }
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-black/[0.06] px-4 pb-3.5 pt-4 md:px-6">
          <div className="min-w-0">
            <h3 className="text-[17px] font-semibold leading-tight text-[#16181D]">
              Cómo sacar buenas fotos
            </h3>
            <p className="mt-1 text-[13px] leading-snug text-black/50">
              Con buenas fotos, la IA entiende mejor tu producto y todo lo que crees sale mejor.
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/[0.06] hover:text-black/70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8 pt-5 md:px-6">
          <CuerpoDeConsejos enVentana />
        </div>
        <div
          className="flex shrink-0 justify-end border-t border-black/[0.06] bg-white px-4 pt-3 md:px-6"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        >
          <button
            type="button"
            onClick={onCerrar}
            className="inline-flex h-11 w-full items-center justify-center rounded-full bg-brand-primary px-5 text-[15px] font-semibold text-white shadow-[0_6px_16px_-6px_rgb(var(--brand-primary-rgb)_/_0.55)] transition-colors hover:bg-brand-primary-hover sm:h-10 sm:w-auto sm:text-[14px]"
          >
            Seguir cargando
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
